import { useEffect, useMemo, useState } from "react";
import PageHead from "@/components/PageHead";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Search,

  Home,
  BarChart3,
  FileText,
  Trophy,
  Gift,
  PlayCircle,
  ArrowRight,
  Loader2,
  ClipboardList,
  Clock,
  FileBarChart,
  Share2,
  MessageCircle,
  Flame,
  Star,
  CheckCircle2,
  LogOut,
  Menu,
  Sparkles,
  ImageIcon,
  Wallet,
  TrendingUp,
  Compass,
  Target,
  Briefcase,
  FileSignature,
  Mic,
  AudioLines,
  Zap,
  Rocket,
  Heart,
  Building2,
  Settings2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

import { User as UserIcon, Settings as SettingsIcon, ScrollText } from "lucide-react";
import AppShell from "@/components/shell/AppShell";
import SidebarSavedCareers from "@/components/dashboard/SidebarSavedCareers";
import { ResultsReadyBanner } from "@/components/dashboard/ResultsReadyBanner";

import VisionBoardCta from "@/components/VisionBoardCta";
import { useAuth } from "@/contexts/AuthContext";
import { useDisplayName } from "@/hooks/useDisplayName";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { fetchMyXp, levelFor, type XpSummary } from "@/lib/xp";
import { analytics } from "@/lib/analytics";
import { cloudStorage } from "@/lib/cloudStore";
import { useAssessmentEntry } from "@/hooks/useAssessmentEntry";
import { useCareerDna } from "@/hooks/useCareerDna";

type AssessmentRow = {
  id: string;
  status: "in_progress" | "completed" | "scored";
  age_group: string;
  created_at: string;
  scored_at: string | null;
  completed_at: string | null;
  trait_scores: Record<string, { score: number; count: number }> | null;
};

const STATUS_META: Record<
  AssessmentRow["status"],
  { label: string; className: string; icon: typeof CheckCircle2 }
> = {
  in_progress: { label: "In Progress", className: "bg-warning/15 text-warning border-warning/30", icon: Clock },
  completed: { label: "Completed", className: "bg-info/15 text-info border-info/30", icon: CheckCircle2 },
  scored: { label: "Scored", className: "bg-primary/15 text-primary border-primary/30", icon: Trophy },
};

const NAV_ITEMS = [
  { to: "/command-center", label: "Future Command Center™", icon: Home },
  { to: "/report", label: "Your Story", icon: FileText },
  { to: "/journey", label: "My Growth Journey", icon: Sparkles },
  { to: "/vision-board", label: "Vision Board", icon: Target },
  { to: "/coach", label: "YoMA™ — Your Mentor Assistant", icon: MessageCircle },
  { to: "/card", label: "Career Identity Card", icon: ImageIcon },
  { to: "/clarity-card", label: "Clarity Card — share it", icon: ImageIcon },
  { to: "/refer", label: "Invite Friends", icon: Gift },
  { to: "/leaderboard", label: "Leaderboards", icon: Trophy },
  { to: "/careers?minGrowth=20&sort=growth", label: "Explore High Potential Careers", icon: TrendingUp },
  { to: "/start", label: "Retake Assessment", icon: Compass },
];

// Full catalog — user picks which to pin to Shortcuts
const SHORTCUTS_CATALOG = [
  { id: "command", to: "/command-center", label: "Future Command Center™", icon: Home },
  { id: "journey", to: "/journey", label: "My Growth Journey", icon: Sparkles },
  { id: "vision", to: "/vision-board", label: "Vision Board", icon: Target },
  { id: "report", to: "/report", label: "Your Story", icon: FileText },
  { id: "card", to: "/card", label: "Career Identity Card", icon: ImageIcon },
  { id: "clarity", to: "/clarity-card", label: "Clarity Card — share it", icon: ImageIcon },
  { id: "refer", to: "/refer", label: "Invite friends — earn rewards", icon: Gift },
  { id: "affiliate", to: "/affiliate", label: "Creator affiliate program", icon: Wallet },
  { id: "leaderboard", to: "/leaderboard", label: "Leaderboards", icon: Trophy },
  { id: "highgrowth", to: "/careers?minGrowth=20&sort=growth", label: "Explore High Potential Careers", icon: TrendingUp },
  { id: "retake", to: "/start", label: "Retake Assessment", icon: Compass },
  { id: "careers", to: "/careers", label: "Browse All Careers", icon: Briefcase },
  { id: "settings", to: "/settings", label: "My Settings", icon: SettingsIcon },
  { id: "profile", to: "/profile", label: "My Profile", icon: UserIcon },
] as const;
type ShortcutId = (typeof SHORTCUTS_CATALOG)[number]["id"];

const DEFAULT_SHORTCUTS: ShortcutId[] = ["command", "journey", "vision", "report", "highgrowth"];
const SHORTCUTS_LS_KEY = "wekit:dashboard:shortcuts:v1";

// Every one of these has shipped and has a live route. They were previously
// listed as "Coming Soon", which was stale — the Pro gate on each route
// already handles access, so advertising them as unavailable was simply
// inaccurate. Kept together as a group of advanced tools.
const MORE_TOOLS = [
  { to: "/portfolio", label: "My Portfolio · Human Potential Transcript", icon: ScrollText },
  { to: "/opportunities", label: "Opportunities", icon: Briefcase },
  { to: "/resume", label: "Resume", icon: FileSignature },
  { to: "/interview", label: "Mock Interview", icon: Mic },
  { to: "/journal", label: "Voice Journal", icon: AudioLines },
  { to: "/challenges", label: "Daily Challenge", icon: Zap },
  { to: "/growth-plan", label: "30-Day Plan", icon: Flame },
];


const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // `?completed=1` is set by the end of the v2 assessment run.
  const dashboardParams = new URLSearchParams(location.search);
  const justCompleted = dashboardParams.get("completed") === "1";
  const completedBand = dashboardParams.get("band");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<AssessmentRow[]>([]);
  const { name: displayName, refresh: refreshDisplayName } = useDisplayName();
  const [xp, setXp] = useState<XpSummary | null>(null);
  const [portfolioHref, setPortfolioHref] = useState<string>("/portfolio");
  const [shortcutIds, setShortcutIds] = useState<ShortcutId[]>(() => {
    if (typeof window === "undefined") return DEFAULT_SHORTCUTS;
    try {
      const raw = cloudStorage.getItem(SHORTCUTS_LS_KEY);
      if (!raw) return DEFAULT_SHORTCUTS;
      const parsed = JSON.parse(raw) as string[];
      const valid = parsed.filter((id) => SHORTCUTS_CATALOG.some((s) => s.id === id)) as ShortcutId[];
      return valid.length ? valid : DEFAULT_SHORTCUTS;
    } catch {
      return DEFAULT_SHORTCUTS;
    }
  });

  useEffect(() => {
    try {
      cloudStorage.setItem(SHORTCUTS_LS_KEY, JSON.stringify(shortcutIds));
    } catch {
      /* ignore */
    }
  }, [shortcutIds]);

  const toggleShortcut = (id: ShortcutId) =>
    setShortcutIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const activeShortcuts = SHORTCUTS_CATALOG.filter((s) => shortcutIds.includes(s.id));

  useEffect(() => {
    analytics.dashboardView();
    if (!user) return;
    let active = true;
    (async () => {
      const [{ data: aData }, { data: pData }, xpData] = await Promise.all([
        supabase
          .from("assessments")
          .select("id, status, age_group, created_at, scored_at, completed_at, trait_scores")
          .eq("user_id", user.id)
          .neq("status", "archived")
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("full_name, email, avatar_url, referral_handle, portfolio_public")
          .eq("id", user.id)
          .maybeSingle(),
        fetchMyXp(user.id),
      ]);
      if (!active) return;
      setRows((aData as AssessmentRow[]) ?? []);
      refreshDisplayName(
        pData?.full_name || pData?.email?.split("@")[0] || user.email?.split("@")[0] || null,
      );
      const handle = (pData as any)?.referral_handle;
      const isPublic = (pData as any)?.portfolio_public;
      setPortfolioHref("/portfolio");
      void handle; void isPublic;
      setXp(xpData);
      setLoading(false);

    })();
    return () => {
      active = false;
    };
  }, [user]);

  const latestScored = rows.find((r) => r.status === "scored");
  const inProgress = rows.find((r) => r.status === "in_progress");
  const completedCount = rows.filter((r) => r.status === "scored" || r.status === "completed").length;
  const hasScored = !!latestScored;
  // Careers that fit, straight off the sealed Career DNA™ payload — no re-ranking here.
  const { data: careerDna } = useCareerDna();
  const dnaMatches = careerDna?.matches ?? [];
  // Resume must land on the half-finished walk, not back at the band picker.
  const entry = useAssessmentEntry();
  const resumeTo = entry.state === "in_progress" ? entry.to : "/start";

  const level = useMemo(() => (xp ? levelFor(xp.totalXp) : null), [xp]);
  const initials = useMemo(
    () =>
      displayName
        .split(" ")
        .map((n) => n[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase(),
    [displayName],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Everything the old second sidebar carried, now a panel inside the one
  // app shell — no duplicate navigation, no lost destination.
  const QuickAccess = (
    <>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl border border-border/50 bg-card/50 p-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3 px-2">
            Go anywhere
          </p>
          <nav className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                    active
                      ? "bg-primary/10 text-primary border border-primary/20 shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="rounded-3xl border border-border/50 bg-card/50 p-5">

          <div className="flex items-center justify-between mb-3 px-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
              <Rocket className="h-3 w-3 text-primary" /> Shortcuts
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Customize shortcuts"
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                >
                  <Settings2 className="h-3 w-3" /> Edit
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-3">
                <p className="text-xs font-semibold mb-2">Customize Shortcuts</p>
                <p className="text-[11px] text-muted-foreground mb-3">
                  Pick which shortcuts appear here.
                </p>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {SHORTCUTS_CATALOG.map((s) => {
                    const checked = shortcutIds.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleShortcut(s.id)}
                          aria-label={s.label}
                        />
                        <s.icon className="h-4 w-4 text-primary/80 shrink-0" />
                        <span className="truncate">{s.label}</span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setShortcutIds(DEFAULT_SHORTCUTS)}
                  className="mt-3 min-h-11 w-full text-[11px] text-muted-foreground hover:text-foreground underline"
                >
                  Reset to defaults
                </button>
              </PopoverContent>
            </Popover>
          </div>
          <nav className="space-y-0.5">
            {activeShortcuts.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground/70 italic">
                No shortcuts pinned. Click <span className="font-semibold">Edit</span> to add some.
              </p>
            ) : (
              activeShortcuts.map((s) => {
                const active = location.pathname + location.search === s.to || location.pathname === s.to;
                return (
                  <Link
                    key={s.id}
                    to={s.to}
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    }`}
                  >
                    <s.icon className="h-4 w-4 text-primary/80 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </Link>
                );
              })
            )}
          </nav>
        </div>

        <div className="rounded-3xl border border-border/50 bg-card/50 p-5 space-y-6">
          <SidebarSavedCareers />

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3 px-2">
              More Tools
            </p>

          <nav className="space-y-0.5">
            {MORE_TOOLS.map((s) => {
              const active = location.pathname === s.to;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                    active
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
                >
                  <s.icon className="h-4 w-4 text-primary/80 shrink-0" />
                  <span className="truncate">{s.label}</span>
                </Link>
              );
            })}
          </nav>
          </div>
        </div>
      </div>
    </>

  );


  return (
    <AppShell title="Tools & History">
      <PageHead
        title="Tools & History | WeKIT™ Career Clarity 360"
        description="Your advanced WeKIT™ tools — history, rewards, careers explorer and utilities."
        path="/dashboard"
        noIndex
      />
      <div className="space-y-6 sm:space-y-8">
            {/* ═══ JUST FINISHED THE ASSESSMENT ═══ */}
            {justCompleted && <ResultsReadyBanner bandParamValue={completedBand} />}


            {/* ═══ HERO ═══ */}
            <section className="grid gap-6 lg:grid-cols-[1fr_auto] items-start">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start">
                {level && (
                  <div className="relative h-24 w-20 sm:h-28 sm:w-24 shrink-0 rounded-2xl overflow-hidden border border-border/60 bg-gradient-to-br from-primary/15 to-accent/10 flex flex-col items-center justify-end p-2">
                    <div className="absolute inset-0 flex items-center justify-center text-4xl sm:text-5xl opacity-90">
                      {level.current.emoji}
                    </div>
                    <span className="relative z-10 text-[9px] font-bold uppercase tracking-widest text-warning-foreground bg-[hsl(var(--warning))] rounded-full px-2 py-0.5">
                      Level {Math.max(1, [...Array(7)].findIndex((_, i) => i > 0 && (xp?.totalXp ?? 0) < (i * 100)) || 1)}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Tools &amp; History
                  </p>
                  <h1 className="mt-2 font-display text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight break-words">
                    Welcome, <span className="gradient-text">{displayName}</span>
                  </h1>
                  <p className="mt-3 sm:mt-4 text-muted-foreground max-w-xl text-sm sm:text-base leading-relaxed">
                    Your advanced tools, history and rewards live here. Your story
                    and your next step live in your{" "}
                    <Link to="/command-center" className="text-primary hover:underline font-medium">
                      Future Command Center™
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 w-full lg:w-auto">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full h-12 px-6 sm:px-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/20"
                >
                  <Link to="/start">
                    {hasScored ? "Retake Assessment" : "Start Assessment"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>


            </section>


            {/* ═══ EVERYTHING, ONE PLACE ═══ */}
            <section aria-labelledby="quick-access-heading">
              <h2 id="quick-access-heading" className="font-display text-2xl font-bold mb-5">
                Everything in one place
              </h2>
              {QuickAccess}
            </section>

            {/* ═══ STATS ROW ═══ */}
            <section className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {/* on tablet, Level card spans full width */}

              {/* Level/XP card */}
              {level && xp && (
                <Card className="border-border/50 bg-gradient-to-br from-primary/10 via-card to-card overflow-hidden sm:col-span-2 lg:col-span-1">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Your Current Level
                        </p>
                        <p className="mt-1 font-display text-3xl font-bold gradient-text">
                          {level.current.name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Total XP
                        </p>
                        <p className="mt-1 font-display text-3xl font-bold">{xp.totalXp}</p>
                      </div>
                    </div>

                    {level.next && (
                      <div>
                        <div className="flex justify-between text-[11px] mb-1.5">
                          <span className="text-muted-foreground">
                            Next: <span className="text-foreground font-semibold uppercase tracking-wider">{level.next.name}</span>
                          </span>
                          <span className="text-primary font-mono">
                            {xp.totalXp}/{level.next.min} XP
                          </span>
                        </div>
                        <Progress value={level.progressPct} className="h-2" />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="flex items-center gap-2.5 rounded-xl bg-background/60 border border-border/40 px-3 py-2">
                        <div className="h-8 w-8 rounded-full bg-[hsl(var(--warning))]/15 flex items-center justify-center">
                          <Flame className="h-4 w-4 text-[hsl(var(--warning))]" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Streak</p>
                          <p className="text-sm font-display font-bold">{xp.currentStreak} days</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 rounded-xl bg-background/60 border border-border/40 px-3 py-2">
                        <div className="h-8 w-8 rounded-full bg-accent/15 flex items-center justify-center">
                          <Star className="h-4 w-4 text-accent" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Best</p>
                          <p className="text-sm font-display font-bold">{xp.longestStreak} days</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assessments taken */}
              <Card className="border-border/50 bg-card/60">
                <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-accent/15 flex items-center justify-center">
                    <ClipboardList className="h-6 w-6 text-accent" />
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Assessments Taken
                  </p>
                  <p className="font-display text-5xl font-bold text-accent">{completedCount}</p>

                  {dnaMatches.length > 0 && (
                    <div className="w-full pt-1">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Careers that fit you
                      </p>
                      <ul className="mt-2 flex flex-col gap-1.5">
                        {dnaMatches.slice(0, 3).map((m) => (
                          <li key={m.id || m.name}>
                            <Link
                              to={`/careers/${encodeURIComponent(m.id)}`}
                              className="flex min-h-[36px] items-center justify-between gap-2 rounded-xl border border-border/40 bg-background/60 px-3 py-1.5 text-left text-xs text-foreground/90 transition-colors hover:border-accent/50 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <span className="truncate">{m.name}</span>
                              <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/assessment/v2/result"
                        className="mt-2 inline-flex min-h-[32px] items-center gap-1 text-[11px] font-medium text-accent hover:underline"
                      >
                        See all matches
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Last activity */}
              <Card className="border-border/50 bg-gradient-to-br from-[hsl(var(--warning))]/8 via-card to-card">
                <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--warning))]/15 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-[hsl(var(--warning))]" />
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Last Activity
                  </p>
                  <p className="font-display text-2xl font-bold">
                    {rows[0]
                      ? formatDistanceToNow(new Date(rows[0].created_at), { addSuffix: false })
                      : "—"}
                  </p>
                  {inProgress && (
                    <Badge className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30 text-[10px] uppercase tracking-wider">
                      In Progress
                    </Badge>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* ═══ YOUR CAREER ═══ */}
            <section>

              <div className="flex items-center gap-4 mb-5">
                <h2 className="font-display text-2xl font-bold">Your Career</h2>
                <div className="hidden sm:flex items-center gap-1 rounded-full border border-border/50 bg-card/50 px-2 py-1">
                  {[FileBarChart, FileText, Share2, MessageCircle].map((Icon, i) => (
                    <span key={i} className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                  ))}
                </div>
              </div>

              {(() => {
                const latest = rows.find((r) => r.status === "scored" || r.status === "completed");
                const highlight = latest ? "career assessment" : null;
                return (
                  <div className="mb-5">
                    <VisionBoardCta
                      context="dashboard"
                      variant="inline"
                      highlight={highlight}
                    />
                  </div>
                );
              })()}

              <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">

                {(() => {
                  const latestScored = rows.find((r) => r.status === "scored" || r.status === "completed");
                  const reportHref = latestScored ? `/report?assessment=${latestScored.id}` : "/report";
                  return (
                <Link to={reportHref} className="group">
                  <Card className="h-full border-border/50 bg-card/60 hover:border-primary/40 hover:-translate-y-1 transition-all">
                    <CardContent className="p-6 space-y-4">
                      <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center">
                        <FileBarChart className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold">Your Story</h3>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          {latestScored
                            ? "Your Living Report™ — your story, your Career DNA™, and the futures that fit it."
                            : "Your Living Report™ opens as a letter, then your Career DNA™ and the futures that fit it."}
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                        Open Your Story <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                  );
                })()}

                <Link to="/report#share" className="group">
                  <Card className="h-full border-[hsl(var(--warning))]/30 bg-gradient-to-br from-[hsl(var(--warning))]/8 via-card to-card hover:-translate-y-1 transition-all">
                    <CardContent className="p-6 space-y-4">
                      <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--warning))]/20 flex items-center justify-center">
                        <Share2 className="h-6 w-6 text-[hsl(var(--warning))]" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold">Family Share</h3>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          Send your report straight to parents or mentors on WhatsApp or email.
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[hsl(var(--warning))]">
                        Share Now <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/coach" className="group">
                  <Card className="h-full border-border/50 bg-card/60 hover:border-primary/40 hover:-translate-y-1 transition-all">
                    <CardContent className="p-6 space-y-4">
                      <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center">
                        <MessageCircle className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold">YoMA™</h3>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          Your Mentor Assistant — an AI companion grounded in your report.
                          It helps you reflect and prepare for real mentoring conversations.
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                        Talk it through <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>


            {/* ═══ PICK UP WHERE YOU LEFT OFF ═══ */}
            {inProgress && (
              <Card className="border border-primary/30 bg-gradient-to-r from-primary/8 via-card to-card overflow-hidden">
                <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/15">
                    <PlayCircle className="h-7 w-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-bold">Pick up where you left off</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Finish your assessment to unlock your story.
                    </p>
                  </div>
                  <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 font-semibold">
                    <Link to={resumeTo}>
                      Resume Now <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* ═══ EXPLORE CAREERS ═══ */}
            <section>
              <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold">Explore Other Careers</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Browse 100+ high-growth careers. See a day in the life, salary, growth outlook & the downsides.
                  </p>
                </div>
                <Button asChild size="lg" className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-95 font-semibold shrink-0">
                  <Link to="/careers">
                    Explore Careers <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <Link to="/careers" className="group block">
                <Card className="border border-primary/30 bg-gradient-to-br from-primary/12 via-accent/8 to-card hover:-translate-y-1 transition-all overflow-hidden">
                  <CardContent className="p-6 sm:p-8">
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      {[
                        { icon: Clock, label: "A Day in the Life", desc: "Hour-by-hour reality of the role" },
                        { icon: TrendingUp, label: "Growth & Salary", desc: "Demand outlook to 2030 & pay bands" },
                        { icon: Target, label: "Downsides & Trade-offs", desc: "What no one tells you up front" },
                        { icon: Compass, label: "Skills & Pathways", desc: "How to break in from where you are" },
                      ].map((it) => (
                        <div key={it.label} className="flex items-start gap-3 rounded-2xl bg-background/40 border border-border/40 p-4">
                          <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/15 flex items-center justify-center">
                            <it.icon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-display font-bold text-sm">{it.label}</p>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                      Open Careers Explorer <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </section>


            {/* ═══ REFER & EARN ═══ */}
            <section>
              <div className="flex items-center gap-4 mb-5">
                <h2 className="font-display text-2xl font-bold">Refer & Earn</h2>
              </div>
              <Card className="border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card overflow-hidden">
                <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="h-14 w-14 shrink-0 rounded-2xl bg-primary/15 flex items-center justify-center">
                    <Gift className="h-7 w-7 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-bold">Invite friends, unlock premium</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      Share your link and earn rewards every time a friend joins WeKIT™ and starts their journey.
                    </p>
                  </div>
                  <Button asChild size="lg" className="rounded-full bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-warning-foreground font-semibold shrink-0">
                    <Link to="/refer">
                      Invite Friends <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </section>




            {/* ═══ HISTORY & MILESTONES ═══ */}
            <section>
              <h2 className="font-display text-2xl font-bold mb-5">History & Milestones</h2>
              {rows.length === 0 ? (
                <Card className="border-border/50">
                  <CardContent className="p-10 text-center text-muted-foreground">
                    No assessments yet. Take your first one to begin.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status];
                    const Icon = meta.icon;
                    return (
                      <Card key={r.id} className="border-border/50 bg-card/60 hover:border-primary/30 transition-colors">
                        <CardContent className="p-5 flex items-center gap-4">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${meta.className}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-display font-bold text-base">WeKIT™ Career Clarity 360</span>
                              <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-border/60">
                                {r.age_group}
                              </Badge>
                              <Badge className={`${meta.className} text-[10px] uppercase tracking-wider`}>
                                {meta.label}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Started {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                              {r.status === "in_progress" && (
                                <span className="text-primary"> • Resume to unlock your report</span>
                              )}
                            </p>
                          </div>
                          {r.status === "scored" ? (
                            <Button size="sm" variant="outline" asChild className="rounded-full">
                              <Link to={`/report?assessment=${r.id}`}>View</Link>
                            </Button>
                          ) : r.status === "in_progress" ? (
                            <Button size="sm" asChild className="rounded-full bg-primary hover:bg-primary/90">
                              <Link to={resumeTo}>Resume</Link>
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" asChild className="rounded-full min-h-11 px-4">
                              <Link to={`/report?assessment=${r.id}`}>Continue</Link>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ═══ SOCIAL IMPACT STRIP — NAPOLEON ROBERTS FOUNDATION ═══ */}
            <section>
              <Card className="border border-primary/20 bg-gradient-to-r from-primary/8 via-card to-accent/8 overflow-hidden">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15">
                        <Heart className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
                          Powered by Napoleon Roberts Foundation · Since 2011
                        </p>
                        <p className="text-sm font-medium leading-snug mt-0.5 truncate">
                          Mentoring &amp; purpose-led careers for every underrepresented young person.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href="https://www.napoleonrobertsfoundation.org/volunteer"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/20 transition-colors"
                      >
                        <Heart className="h-3.5 w-3.5" /> Volunteer
                      </a>
                      <a
                        href="https://www.napoleonrobertsfoundation.org/partner"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-2 text-xs font-bold text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/20 transition-colors"
                      >
                        <Building2 className="h-3.5 w-3.5" /> Corporates / NGOs
                      </a>
                      <a
                        href="https://www.napoleonrobertsfoundation.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                      >
                        Learn more <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
      </div>
    </AppShell>
  );
};

export default Dashboard;
